import { Goal, initialDateFormat, Timestamp } from '@griply/types';
import { getStartTillEndLabel, getWeekStartsOn } from '@griply/utils';
import { useUpdateGoal } from 'features/goals';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'shared/assets/icons';
import {
  DatepickerWithOptions,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options';
import { useToday } from 'shared/contexts/today';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useUser } from 'shared/hooks/use-user';

import { MetaRow } from './meta-row';

export type StartDatePickerRowProps = {
  goal: Goal;
  isEditable: boolean;
};

export const StartDatePickerRow: React.FC<StartDatePickerRowProps> = ({
  goal,
  isEditable,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const user = useUser();
  const { submit: updateGoal } = useUpdateGoal();
  const startDateRef = useRef<HTMLSpanElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const startDateText = goal.startDate
    ? getStartTillEndLabel({
        startDate: goal.startDate,
        isShort: true,
        today,
        t,
      })
    : t('forms.goal.fields.start-date.placeholder');

  const onStartDateChange = (date?: Timestamp) => {
    updateGoal({ id: goal.id, startDate: date ?? null });
    closeMenu();
  };

  return (
    <>
      <MetaRow
        ref={startDateRef}
        icon={Calendar}
        label={t('forms.goal.fields.start-date.label')}
        isClickable={isEditable}
        isPlaceholder={!goal.startDate}
        onClick={openMenu}
      >
        {startDateText}
      </MetaRow>

      {menuOpen && (
        <DatepickerWithOptions
          referenceElement={startDateRef}
          value={goal.startDate ?? undefined}
          onChange={onStartDateChange}
          dateFormat={user?.settings?.dateFormat ?? initialDateFormat}
          weekStartsOn={getWeekStartsOn(user)}
          icon={IconOptions.Calendar}
          canRemoveValue={!!goal.startDate}
          onOutside={closeMenu}
        />
      )}
    </>
  );
};
